import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useNotification } from "../contexts/NotificationContext";
import { leadService } from "../services/leadService";
import { interactionService } from "../services/interactionService";
import { contractService } from "../services/contractService";
import InteractionForm from "../components/interactions/InteractionForm";
import InteractionList from "../components/interactions/InteractionList";
import {
  ArrowLeft,
  Mail,
  Phone,
  Building,
  Briefcase,
  Calendar,
  Target,
  ClipboardList,
  Clock,
  ArrowUpRight,
  TrendingUp,
  Coins,
  FileText,
  PlusCircle,
} from "lucide-react";
import { LEAD_STATUS, REVENUE_CURRENCIES } from "../config/constants";
import { normalizeCurrencyCode } from "../utils/formatters";

const LeadDetailPage = () => {
  const { id } = useParams();
  const { currentUser, isAdmin } = useAuth();
  const { showSuccess, showError } = useNotification();
  const DEFAULT_CURRENCY = "XOF";
  const [lead, setLead] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInteractionForm, setShowInteractionForm] = useState(false);
  const [editingRevenue, setEditingRevenue] = useState(false);
  const [revenueForm, setRevenueForm] = useState({
    expectedRevenue: "",
    revenueCurrency: DEFAULT_CURRENCY,
    expectedCloseDate: "",
  });
  const [contracts, setContracts] = useState([]);
  const [showContractForm, setShowContractForm] = useState(false);
  const [contractForm, setContractForm] = useState({
    amount: "",
    currency: DEFAULT_CURRENCY,
    signedAt: new Date().toISOString().split("T")[0],
    notes: "",
  });

  useEffect(() => {
    loadLeadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadLeadData = async () => {
    try {
      setLoading(true);
      const leadData = await leadService.getLeadById(id);
      const interactionsData = await interactionService.getInteractionsByLead(
        id
      );
      const contractsData = await contractService.getContractsByLead(id);
      setLead(leadData);
      setInteractions(interactionsData);
      setContracts(contractsData);
      if (leadData) {
        const defaultCurrency = normalizeCurrencyCode(
          leadData.revenueCurrency ||
            contractsData[0]?.currency ||
            DEFAULT_CURRENCY,
          DEFAULT_CURRENCY
        );
        setRevenueForm({
          expectedRevenue:
            leadData.expectedRevenue !== undefined &&
            leadData.expectedRevenue !== null
              ? String(leadData.expectedRevenue)
              : "",
          revenueCurrency: defaultCurrency,
          expectedCloseDate: toInputDate(leadData.expectedCloseDate),
        });
        setContractForm((prev) => ({
          ...prev,
          amount: "",
          currency: defaultCurrency,
          signedAt: new Date().toISOString().split("T")[0],
          notes: "",
        }));
      }
    } catch (error) {
      console.error("Erreur chargement:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRevenueChange = (event) => {
    const { name, value } = event.target;
    setRevenueForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRevenueSave = async () => {
    if (!lead) return;
    try {
      const payload = {
        expectedRevenue: revenueForm.expectedRevenue,
        revenueCurrency: normalizeCurrencyCode(
          revenueForm.revenueCurrency,
          DEFAULT_CURRENCY
        ),
        expectedCloseDate: revenueForm.expectedCloseDate || null,
      };

      const result = await leadService.updateLead(lead.id, payload);
      if (result.success) {
        setLead((prev) =>
          prev
            ? {
                ...prev,
                expectedRevenue: payload.expectedRevenue
                  ? Number(payload.expectedRevenue)
                  : 0,
                revenueCurrency: normalizeCurrencyCode(
                  payload.revenueCurrency,
                  DEFAULT_CURRENCY
                ),
                expectedCloseDate: payload.expectedCloseDate
                  ? new Date(payload.expectedCloseDate)
                  : null,
              }
            : prev
        );
        setRevenueForm({
          expectedRevenue:
            payload.expectedRevenue && payload.expectedRevenue !== ""
              ? String(Number(payload.expectedRevenue))
              : "",
          revenueCurrency: normalizeCurrencyCode(
            payload.revenueCurrency,
            DEFAULT_CURRENCY
          ),
          expectedCloseDate: payload.expectedCloseDate || "",
        });
        showSuccess("Chiffre d'affaires mis ? jour");
        setEditingRevenue(false);
      } else {
        showError("Impossible de mettre ? jour le chiffre d'affaires");
      }
    } catch (error) {
      console.error("Erreur mise ? jour chiffre d'affaires:", error);
      showError("Erreur lors de l'enregistrement du chiffre d'affaires");
    }
  };

  const resetRevenueForm = () => {
    if (!lead) {
      setEditingRevenue(false);
      return;
    }
    setRevenueForm({
      expectedRevenue:
        lead.expectedRevenue !== undefined && lead.expectedRevenue !== null
          ? String(lead.expectedRevenue)
          : "",
      revenueCurrency: normalizeCurrencyCode(
        lead.revenueCurrency,
        DEFAULT_CURRENCY
      ),
      expectedCloseDate: toInputDate(lead.expectedCloseDate),
    });
    setEditingRevenue(false);
  };

  const handleContractChange = (event) => {
    const { name, value } = event.target;
    setContractForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleContractSubmit = async () => {
    if (!lead) return;
    if (lead.status !== LEAD_STATUS.CONVERTED) {
      showError("Le prospect doit être converti avant d'ajouter un contrat.");
      return;
    }

    const amountValue = sanitizeAmount(contractForm.amount);
    if (amountValue <= 0) {
      showError("Veuillez saisir un montant de contrat valide.");
      return;
    }

    if (!contractForm.signedAt) {
      showError("Veuillez renseigner la date de signature du contrat.");
      return;
    }

    try {
      await contractService.createContract(
        lead.id,
        contractForm,
        currentUser.uid
      );
      showSuccess("Contrat enregistré avec succès");
      const updatedContracts = await contractService.getContractsByLead(
        lead.id
      );
      setContracts(updatedContracts);
      setShowContractForm(false);
      setContractForm((prev) => ({
        amount: "",
        currency: prev.currency,
        signedAt: new Date().toISOString().split("T")[0],
        notes: "",
      }));
    } catch (error) {
      console.error("Erreur création contrat:", error);
      showError(error?.message || "Impossible d'enregistrer le contrat");
    }
  };

  const handleContractCancel = () => {
    setShowContractForm(false);
    setContractForm((prev) => ({
      amount: "",
      currency: prev.currency,
      signedAt: new Date().toISOString().split("T")[0],
      notes: "",
    }));
  };

  const handleStatusChange = async (newStatus) => {
    const result = await leadService.changeStatus(
      id,
      newStatus,
      currentUser.uid
    );
    if (result.success) {
      setLead((prev) => ({ ...prev, status: newStatus }));
    }
  };

  const handleInteractionCreated = async () => {
    await loadLeadData();
    setShowInteractionForm(false);
  };

  const recommendedActions = useMemo(() => {
    if (!lead) return [];

    const base = [];
    switch (lead.status) {
      case LEAD_STATUS.NEW:
        base.push({
          title: "Programmer un premier contact",
          message:
            "Planifiez un appel d'introduction et personnalisez votre argumentaire avec les informations recueillies.",
        });
        break;
      case LEAD_STATUS.CONTACTED:
        base.push({
          title: "Envoyer un recap",
          message:
            "Confirmez les besoins remontes par email et proposez un rendez-vous de qualification.",
        });
        break;
      case LEAD_STATUS.QUALIFIED:
        base.push({
          title: "Partager une proposition",
          message:
            "Formalisez une offre detaillee et partagez un calendrier de deploiement.",
        });
        break;
      case LEAD_STATUS.NEGOTIATION:
        base.push({
          title: "Preparer la negociation",
          message:
            "Identifiez les concessions possibles et securisez les validations internes necessaires.",
        });
        break;
      default:
        break;
    }

    base.push({
      title: "Mettre a jour la fiche",
      message:
        "Consignez les echanges cles pour assurer une continuite de suivi optimale.",
    });

    return base;
  }, [lead]);

  const contractTotals = useMemo(() => {
    const baseCurrency = normalizeCurrencyCode(
      lead?.revenueCurrency || revenueForm.revenueCurrency || DEFAULT_CURRENCY,
      DEFAULT_CURRENCY
    );

    if (!contracts.length) {
      return {
        total: 0,
        currency: baseCurrency,
        count: 0,
        lastSigned: null,
      };
    }

    const totals = contracts.reduce(
      (acc, contract) => {
        const amount = sanitizeAmount(contract.amount);
        acc.total += amount;
        acc.count += 1;

        const contractCurrency = normalizeCurrencyCode(
          contract.currency,
          baseCurrency
        );
        if (!acc.currency && contract.currency) {
          acc.currency = contractCurrency;
        }

        const signedDate =
          toJsDate(contract.signedAt) || toJsDate(contract.createdAt);
        if (signedDate && (!acc.lastSigned || signedDate > acc.lastSigned)) {
          acc.lastSigned = signedDate;
        }

        return acc;
      },
      {
        total: 0,
        currency: normalizeCurrencyCode(contracts[0]?.currency, baseCurrency),
        count: 0,
        lastSigned: null,
      }
    );

    return {
      ...totals,
      currency: normalizeCurrencyCode(totals.currency, baseCurrency),
    };
  }, [contracts, lead, revenueForm.revenueCurrency]);

  const currencyCode = normalizeCurrencyCode(
    contractTotals.currency ||
      lead?.revenueCurrency ||
      revenueForm.revenueCurrency ||
      DEFAULT_CURRENCY,
    DEFAULT_CURRENCY
  );

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: currencyCode,
        minimumFractionDigits: 0,
      }),
    [currencyCode]
  );

  const expectedRevenueFormatted = currencyFormatter.format(
    sanitizeAmount(lead?.expectedRevenue) || 0
  );
  const totalContractsFormatted = currencyFormatter.format(
    contractTotals.total || 0
  );
  const contractsCountLabel = `${contractTotals.count} contrat${
    contractTotals.count > 1 ? "s" : ""
  }`;
  const lastContractFormatted = contractTotals.lastSigned
    ? formatDateSafe(contractTotals.lastSigned)
    : "Aucun";
  const currencyLabel =
    REVENUE_CURRENCIES.find((item) => item.code === currencyCode)?.label ||
    currencyCode;

  const canEditRevenue =
    isAdmin || lead?.assignedToUserId === currentUser?.uid || false;
  const canAddContract =
    canEditRevenue && lead?.status === LEAD_STATUS.CONVERTED;

  useEffect(() => {
    if (!canAddContract && showContractForm) {
      setShowContractForm(false);
    }
  }, [canAddContract, showContractForm]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
        <div className="text-center">
          <div className="spinner" />
          <p className="mt-4 text-sm text-[var(--color-text-secondary)]">
            Chargement de la fiche prospect...
          </p>
        </div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
        <p className="text-sm text-[var(--color-text-secondary)]">
          Prospect introuvable.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg-page)]">
      <main className="container-custom py-8 space-y-8">
        <section className="panel-surface p-6 space-y-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-1">
              <Link
                to="/leads"
                className="inline-flex items-center gap-2 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-brand)]"
              >
                <ArrowLeft className="w-4 h-4" /> Retour à la liste
              </Link>
              <h1 className="text-3xl font-semibold text-[var(--color-text-primary)]">
                {lead.firstName} {lead.lastName}
              </h1>
              <p className="text-sm text-[var(--color-text-secondary)]">
                {lead.jobTitle || "Fonction non renseignée"}{" "}
                {lead.company || "Entreprise non renseignée"}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <StatusSelect value={lead.status} onChange={handleStatusChange} />
              <button
                className="btn btn-outline"
                onClick={() => setShowInteractionForm(true)}
              >
                Ajouter une interaction
              </button>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <InfoTile
              icon={<Mail className="w-4 h-4" />}
              label="Email"
              value={lead.email || "Non renseigné"}
            />
            <InfoTile
              icon={<Phone className="w-4 h-4" />}
              label="T?l?phone"
              value={lead.phone || "Non renseigné"}
            />
            <InfoTile
              icon={<Building className="w-4 h-4" />}
              label="Entreprise"
              value={lead.company || "Non renseignée"}
            />
            <InfoTile
              icon={<Briefcase className="w-4 h-4" />}
              label="Source"
              value={lead.source || "Non renseignée"}
            />
          </div>
        </section>

        <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
          <section className="panel-surface p-6 space-y-6">
            <div className="panel-quiet p-4">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-text-primary)]">
                <Target className="w-4 h-4" /> Informations principales
              </h2>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <DetailBlock
                  title="Date d'entrée"
                  value={formatDateSafe(lead.prospectionDate)}
                  icon={
                    <Calendar className="w-4 h-4 text-[var(--color-text-muted)]" />
                  }
                />
                <DetailBlock
                  title="Statut actuel"
                  value={lead.status}
                  icon={
                    <ClipboardList className="w-4 h-4 text-[var(--color-text-muted)]" />
                  }
                />
              </div>
            </div>

            <section className="panel-quiet p-4 space-y-4">
              <header className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-2 text-sm font-semibold text-[var(--color-text-primary)]">
                  <TrendingUp className="w-4 h-4" /> Chiffre d'affaires &
                  objectifs
                </div>
                <div className="flex flex-wrap gap-2">
                  {canEditRevenue && (
                    <button
                      type="button"
                      className="btn btn-outline btn-xs flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => setShowContractForm((prev) => !prev)}
                      disabled={!canAddContract}
                      title={
                        canAddContract
                          ? "Enregistrer un contrat signé"
                          : "Convertissez le prospect avant d'ajouter un contrat"
                      }
                    >
                      <PlusCircle className="w-3 h-3" />
                      {showContractForm
                        ? "Fermer le formulaire"
                        : "Ajouter un contrat"}
                    </button>
                  )}
                  {canEditRevenue && !editingRevenue && (
                    <button
                      type="button"
                      className="btn btn-outline btn-xs"
                      onClick={() => setEditingRevenue(true)}
                    >
                      Modifier les prévisions
                    </button>
                  )}
                </div>
              </header>

              <div className="grid gap-4 md:grid-cols-2">
                <RevenueMetric
                  icon={<Coins className="w-4 h-4" />}
                  label="Objectif estimé"
                  value={expectedRevenueFormatted}
                  helper="Prévision enregistrée sur le prospect"
                />
                <RevenueMetric
                  icon={<TrendingUp className="w-4 h-4" />}
                  label="Contrats signés"
                  value={totalContractsFormatted}
                  helper={
                    contractTotals.count
                      ? `${contractsCountLabel} Dernier : ${lastContractFormatted}`
                      : "Aucun contrat sign? pour l'instant"
                  }
                />
                <RevenueMetric
                  icon={<Briefcase className="w-4 h-4" />}
                  label="Devise"
                  value={currencyLabel}
                />
                <RevenueMetric
                  icon={<Calendar className="w-4 h-4" />}
                  label="Clôture prévue"
                  value={formatDateSafe(lead.expectedCloseDate)}
                />
              </div>

              {editingRevenue && canEditRevenue && (
                <div className="border border-dashed border-[var(--color-border-soft)] rounded-lg p-4 space-y-4 bg-[var(--color-bg-surface)]">
                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                        CA estimé
                      </label>
                      <input
                        name="expectedRevenue"
                        type="number"
                        step="0.01"
                        min="0"
                        value={revenueForm.expectedRevenue}
                        onChange={handleRevenueChange}
                        className="input-field"
                        placeholder="Montant estimé"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                        Devise
                      </label>
                      <select
                        name="revenueCurrency"
                        value={revenueForm.revenueCurrency}
                        onChange={handleRevenueChange}
                        className="input-field"
                      >
                        {REVENUE_CURRENCIES.map((currency) => (
                          <option key={currency.code} value={currency.code}>
                            {currency.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                        Date de clôture prévue
                      </label>
                      <input
                        name="expectedCloseDate"
                        type="date"
                        value={revenueForm.expectedCloseDate}
                        onChange={handleRevenueChange}
                        className="input-field"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleRevenueSave}
                    >
                      Enregistrer
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={resetRevenueForm}
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}

              {showContractForm && canAddContract && (
                <div className="border border-dashed border-[var(--color-border-soft)] rounded-lg p-4 space-y-4 bg-[var(--color-bg-surface)]">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                        Montant du contrat
                      </label>
                      <input
                        name="amount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={contractForm.amount}
                        onChange={handleContractChange}
                        className="input-field"
                        placeholder="Montant signé"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                        Devise
                      </label>
                      <select
                        name="currency"
                        value={contractForm.currency}
                        onChange={handleContractChange}
                        className="input-field"
                      >
                        {REVENUE_CURRENCIES.map((currency) => (
                          <option key={currency.code} value={currency.code}>
                            {currency.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                        Date de signature
                      </label>
                      <input
                        name="signedAt"
                        type="date"
                        value={contractForm.signedAt}
                        onChange={handleContractChange}
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                        Notes
                      </label>
                      <textarea
                        name="notes"
                        value={contractForm.notes}
                        onChange={handleContractChange}
                        rows={3}
                        className="input-field"
                        placeholder="Observations éventuelles"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleContractSubmit}
                    >
                      Enregistrer le contrat
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={handleContractCancel}
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-text-primary)]">
                    <FileText className="w-4 h-4" /> Historique des contrats
                  </h4>
                  <span className="text-xs text-[var(--color-text-muted)]">
                    {contractsCountLabel}
                  </span>
                </div>

                {contracts.length === 0 ? (
                  <p className="text-sm text-[var(--color-text-muted)]">
                    Aucun contrat signé pour le moment.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[var(--color-text-muted)] border-b border-[var(--color-border-soft)]">
                          <th className="py-2 px-3 font-medium">Montant</th>
                          <th className="py-2 px-3 font-medium">Devise</th>
                          <th className="py-2 px-3 font-medium">Signé le</th>
                          <th className="py-2 px-3 font-medium">Notes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--color-border-soft)]">
                        {contracts.map((contract) => (
                          <tr key={contract.id}>
                            <td className="py-2 px-3 text-[var(--color-text-primary)] font-semibold">
                              {currencyFormatter.format(
                                sanitizeAmount(contract.amount)
                              )}
                            </td>
                            <td className="py-2 px-3 text-[var(--color-text-secondary)]">
                              {contract.currency || currencyCode}
                            </td>
                            <td className="py-2 px-3 text-[var(--color-text-secondary)]">
                              {formatDateSafe(contract.signedAt)}
                            </td>
                            <td className="py-2 px-3 text-[var(--color-text-secondary)]">
                              {contract.notes?.trim() ? contract.notes : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>

            <section className="panel-quiet p-4 space-y-4">
              <header className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
                  Plan d'actions recommandé
                </h3>
                <Clock className="w-4 h-4 text-[var(--color-text-muted)]" />
              </header>
              <ul className="space-y-3">
                {recommendedActions.map((item) => (
                  <li
                    key={item.title}
                    className="panel-quiet p-3 border border-[var(--color-border-soft)]"
                  >
                    <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                      {item.title}
                    </p>
                    <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
                      {item.message}
                    </p>
                  </li>
                ))}
              </ul>
            </section>

            <section className="panel-quiet p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
                  Historique des interactions
                </h3>
                <button
                  className="text-xs font-semibold text-[var(--color-brand)] hover:text-[var(--color-brand-strong)]"
                  onClick={() => setShowInteractionForm((prev) => !prev)}
                >
                  {showInteractionForm ? "Fermer" : "Ajouter"}
                </button>
              </div>

              {showInteractionForm && (
                <div className="panel-quiet p-4 border border-[var(--color-border-soft)] mb-4">
                  <InteractionForm
                    leadId={id}
                    onCreated={handleInteractionCreated}
                    onCancel={() => setShowInteractionForm(false)}
                  />
                </div>
              )}

              <InteractionList leadId={id} />
            </section>
          </section>

          <aside className="space-y-6">
            <section className="panel-surface p-6 space-y-4">
              <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
                Suivi rapide
              </h3>
              <ul className="space-y-3 text-sm text-[var(--color-text-secondary)]">
                <li>
                  <strong className="text-[var(--color-text-primary)]">
                    Source :
                  </strong>{" "}
                  {lead.source || "Non renseignée"}
                </li>
                <li>
                  <strong className="text-[var(--color-text-primary)]">
                    Objectif CA :
                  </strong>{" "}
                  {expectedRevenueFormatted}
                </li>
                <li>
                  <strong className="text-[var(--color-text-primary)]">
                    CA signé :
                  </strong>{" "}
                  {totalContractsFormatted}
                </li>
                <li>
                  <strong className="text-[var(--color-text-primary)]">
                    Contrats :
                  </strong>{" "}
                  {contractsCountLabel}
                </li>
                <li>
                  <strong className="text-[var(--color-text-primary)]">
                    Dernier contrat :
                  </strong>{" "}
                  {contractTotals.count ? lastContractFormatted : "Aucun"}
                </li>
                <li>
                  <strong className="text-[var(--color-text-primary)]">
                    Devise :
                  </strong>{" "}
                  {currencyLabel}
                </li>
                <li>
                  <strong className="text-[var(--color-text-primary)]">
                    Dernière interaction :
                  </strong>{" "}
                  {formatDateSafe(interactions[0]?.createdAt)}
                </li>
              </ul>
            </section>

            <section className="panel-surface p-6 space-y-4">
              <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
                Ressources utiles
              </h3>
              <ul className="space-y-3 text-sm text-[var(--color-text-secondary)]">
                <li className="flex items-center justify-between">
                  <span>Script de qualification</span>
                  <Link
                    to="/docs/scripts"
                    className="inline-flex items-center gap-1 text-xs text-[var(--color-brand)]"
                  >
                    Consulter <ArrowUpRight className="w-3 h-3" />
                  </Link>
                </li>
                <li className="flex items-center justify-between">
                  <span>Modèle proposition</span>
                  <Link
                    to="/docs/proposal"
                    className="inline-flex items-center gap-1 text-xs text-[var(--color-brand)]"
                  >
                    Télécharger <ArrowUpRight className="w-3 h-3" />
                  </Link>
                </li>
              </ul>
            </section>
          </aside>
        </div>
      </main>
    </div>
  );
};

const InfoTile = ({ icon, label, value }) => (
  <div className="panel-quiet p-4">
    <div className="flex items-center gap-2 text-xs text-[var(--color-text-muted)] uppercase">
      {icon}
      <span>{label}</span>
    </div>
    <p className="mt-2 text-sm font-semibold text-[var(--color-text-primary)]">
      {value}
    </p>
  </div>
);

const RevenueMetric = ({ icon, label, value, helper }) => (
  <div className="border border-[var(--color-border-soft)] rounded-lg p-4 bg-[var(--color-bg-surface)]">
    <div className="flex items-center gap-2 text-xs text-[var(--color-text-muted)] uppercase">
      <span className="text-[var(--color-brand)]">{icon}</span>
      <span>{label}</span>
    </div>
    <p className="mt-2 text-lg font-semibold text-[var(--color-text-primary)]">
      {value}
    </p>
    {helper && (
      <p className="text-xs text-[var(--color-text-secondary)] mt-1">
        {helper}
      </p>
    )}
  </div>
);

const DetailBlock = ({ title, value, icon }) => (
  <div className="panel-quiet p-4 flex items-center gap-3">
    <div className="w-8 h-8 rounded-full bg-[var(--color-brand-soft)] text-[var(--color-brand)] flex items-center justify-center">
      {icon}
    </div>
    <div>
      <p className="text-xs text-[var(--color-text-muted)] uppercase">
        {title}
      </p>
      <p className="text-sm font-semibold text-[var(--color-text-primary)]">
        {value}
      </p>
    </div>
  </div>
);

const StatusSelect = ({ value, onChange }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="input-field"
  >
    {Object.values(LEAD_STATUS).map((status) => (
      <option key={status} value={status}>
        {status}
      </option>
    ))}
  </select>
);

const sanitizeAmount = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value.replace(/\s/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const toJsDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") {
    try {
      return value.toDate();
    } catch (error) {
      return null;
    }
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateSafe = (date) => {
  if (!date) return "Non renseignée";
  try {
    if (typeof date.toDate === "function") {
      return date.toDate().toLocaleDateString("fr-FR");
    }
    return new Date(date).toLocaleDateString("fr-FR");
  } catch (error) {
    return "Non renseignée";
  }
};

const toInputDate = (date) => {
  if (!date) return "";
  try {
    const parsed =
      typeof date?.toDate === "function" ? date.toDate() : new Date(date);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toISOString().split("T")[0];
  } catch (error) {
    return "";
  }
};

export default LeadDetailPage;
